// Session Z78 — per-user Section Theme Picker.
//
// Each operator picks which theme (teal / indigo / amber / plum / stone) sits
// on which page's primary `.ns-section`. Choices persist to the backend
// (`/api/settings/section-themes`) so the preference follows them across
// browsers. A tiny pub/sub cache keeps every mounted page in sync when the
// user changes the mapping in Settings.

import { useEffect, useState } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const THEME_OPTIONS = ["teal", "indigo", "amber", "plum", "stone"];

export const PAGE_KEYS = [
  "dashboard",
  "calendar",
  "bookings",
  "contacts",
  "inbox",
  "journeys",
  "properties",
  "statements",
];

export const PAGE_LABELS = {
  dashboard: "Dashboard",
  calendar: "Calendar",
  bookings: "Bookings",
  contacts: "Contacts",
  inbox: "Inbox",
  journeys: "Journeys",
  properties: "Properties",
  statements: "Statements",
};

export const DEFAULT_THEMES = {
  dashboard: "teal",
  calendar: "indigo",
  bookings: "teal",
  contacts: "indigo",
  inbox: "indigo",
  journeys: "plum",
  properties: "teal",
  statements: "teal",
};

let cache = null;
let fetching = null;
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => {
    try { fn(); } catch {}
  });
}

async function fetchThemes() {
  const tok = localStorage.getItem("ns_token");
  if (!tok) return { ...DEFAULT_THEMES };
  try {
    const res = await axios.get(`${BASE}/api/settings/section-themes`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    return { ...DEFAULT_THEMES, ...(res.data?.themes || {}) };
  } catch {
    return { ...DEFAULT_THEMES };
  }
}

function ensureLoaded() {
  if (cache || fetching) return;
  fetching = fetchThemes().then((t) => {
    cache = t;
    fetching = null;
    notify();
  });
}

export function useSectionTheme(pageKey) {
  const [theme, setTheme] = useState(
    () => (cache && cache[pageKey]) || DEFAULT_THEMES[pageKey] || "teal",
  );
  useEffect(() => {
    ensureLoaded();
    const update = () =>
      setTheme((cache && cache[pageKey]) || DEFAULT_THEMES[pageKey] || "teal");
    subscribers.add(update);
    if (cache) update();
    return () => subscribers.delete(update);
  }, [pageKey]);
  return theme;
}

export async function getThemesOnce() {
  if (cache) return { ...cache };
  cache = await fetchThemes();
  return { ...cache };
}

export async function saveThemes(themes) {
  const tok = localStorage.getItem("ns_token");
  const res = await axios.put(
    `${BASE}/api/settings/section-themes`,
    { themes },
    { headers: { Authorization: `Bearer ${tok}` } },
  );
  cache = { ...DEFAULT_THEMES, ...(res.data?.themes || themes) };
  notify();
  return { ...cache };
}

export function resetThemes() {
  cache = null;
  fetching = null;
  notify();
}
